import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiArrowLeft, FiPlayCircle, FiSave } from "react-icons/fi";
import { participantApi } from "../../../api/participant";
import Loader from "../../../components/loader/Loader";

const pickModules = (response) =>
  response?.data?.modules || response?.modules || response?.resourceData || [];

const pickAssessments = (response) => response?.data?.assessments || [];

const pickCourseName = (response) => response?.data?.courseName || "Course";

const SELECTED_MODULE_KEY = "participant:selectedModule";
const ALLOWED_PROGRESS_STATUS = ["failed", "completed", "in_progress", "not_started"];

const moduleCompletionPercent = (modules) => {
  if (!modules.length) return 0;
  const completedCount = modules.filter((moduleItem) => {
    if (typeof moduleItem?.moduleCompletion === "boolean") return moduleItem.moduleCompletion;
    const status = String(moduleItem?.status || "").toLowerCase();
    return status === "completed";
  }).length;
  return Math.round((completedCount / modules.length) * 100);
};

const pickLaunchPath = (response) => {
  const data = response?.data || response?.resourceData || response || {};
  return data?.modulePath || data?.launchPath || data?.url || data?.link || null;
};

const CourseModules = () => {
  const navigate = useNavigate();
  const { trackerId, learningPathId, courseId } = useParams();
  const [progressMap, setProgressMap] = useState({});
  const [statusMap, setStatusMap] = useState({});
  const [scoreMap, setScoreMap] = useState({});

  const { data, isLoading, refetch } = useQuery(
    ["participant-course-modules", trackerId, learningPathId, courseId],
    () => participantApi.getLearningPathCourseModules({ trackerId, learningPathId, courseId }),
    {
      enabled: Boolean(trackerId && learningPathId && courseId),
      staleTime: 30000,
    }
  );

  const modules = useMemo(() => (Array.isArray(pickModules(data)) ? pickModules(data) : []), [data]);
  const assessments = useMemo(() => (Array.isArray(pickAssessments(data)) ? pickAssessments(data) : []), [data]);
  const courseName = pickCourseName(data);
  const completionPercent = moduleCompletionPercent(modules);
  const canTakeAssessments = completionPercent === 100;

  const startModuleMutation = useMutation(participantApi.startModule, {
    onSuccess: (response) => {
      toast.success(response?.message || "Module started.");
      refetch();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to start module.");
    },
  });

  const saveProgressMutation = useMutation(participantApi.saveModuleProgress, {
    onSuccess: (response) => {
      toast.success(response?.message || "Progress saved.");
      refetch();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to save progress.");
    },
  });

  const startModule = (moduleItem, kind = "module") => {
    startModuleMutation.mutate({
      trackerId,
      learningPathId,
      courseId,
      moduleId: moduleItem?.id || moduleItem?.moduleId,
      payload: {},
    }, {
      onSuccess: (response) => {
        const modulePath = pickLaunchPath(response);
        sessionStorage.setItem(
          SELECTED_MODULE_KEY,
          JSON.stringify({
            trackerId,
            learningPathId,
            courseId,
            moduleId: moduleItem?.id || moduleItem?.moduleId,
            kind,
            title: moduleItem?.title || moduleItem?.name || "Learning Item",
            modulePath,
          })
        );

        if (modulePath) {
          navigate(
            `/participant/module-player?trackerId=${encodeURIComponent(String(trackerId || ""))}&learningPathId=${encodeURIComponent(
              String(learningPathId || "")
            )}&courseId=${encodeURIComponent(String(courseId || ""))}`
          );
          return;
        }

        toast.success(response?.message || "Module started.");
        refetch();
      },
    });
  };

  const saveProgress = (moduleItem) => {
    const moduleId = moduleItem?.id || moduleItem?.moduleId;
    const candidateStatus = String(statusMap[moduleId] || moduleItem?.status || "in_progress").toLowerCase();
    const status = ALLOWED_PROGRESS_STATUS.includes(candidateStatus) ? candidateStatus : "in_progress";
    const score = Number(scoreMap[moduleId] ?? moduleItem?.score ?? 0);

    saveProgressMutation.mutate({
      trackerId,
      moduleId,
      courseId,
      status,
      score,
    });
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate(`/participant/courses-and-learning-path/${trackerId}/${learningPathId}`)}
        className="inline-flex items-center gap-2 text-sm text-[#0A3059] mb-4"
      >
        <FiArrowLeft /> Back
      </button>

      <h1 className="text-xl font-semibold text-[#0A3059] mb-1">{courseName}</h1>
      <p className="text-sm text-gray-600 mb-2">Course Modules and Assessments</p>
      <div className="max-w-[980px] mb-5 bg-white border border-gray-100 rounded-xl p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-gray-600">Module Completion</p>
          <p className="text-xs font-semibold text-[#0A3059]">{completionPercent}%</p>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-gray-200 overflow-hidden">
          <div className="h-1.5 bg-[#8CB33E]" style={{ width: `${completionPercent}%` }} />
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white p-6 rounded-xl border border-gray-100">
          <Loader />
        </div>
      ) : modules.length || assessments.length ? (
        <div className="space-y-3 max-w-[980px]">
          {modules.length ? (
            <h2 className="text-sm font-semibold text-gray-700">Modules</h2>
          ) : null}
          {modules.map((moduleItem) => {
            const moduleId = moduleItem?.id || moduleItem?.moduleId;
            const progress = Number(progressMap[moduleId] ?? moduleItem?.progress ?? 0);
            const status = statusMap[moduleId] || moduleItem?.status || "in_progress";
            const score = Number(scoreMap[moduleId] ?? moduleItem?.score ?? 0);

            return (
              <div key={moduleId} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="grow">
                    <p className="text-base font-medium text-gray-800">{moduleItem?.title || moduleItem?.name || "Module"}</p>
                    <p className="text-[11px] text-gray-500 mt-1">{moduleItem?.description || "No description"}</p>

                    <div className="mt-3 max-w-[380px]">
                      <label className="text-xs text-gray-600">Progress (%)</label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={progress}
                        onChange={(event) =>
                          setProgressMap((prev) => ({
                            ...prev,
                            [moduleId]: event.target.value,
                          }))
                        }
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500">{progress}%</p>
                    </div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 max-w-[420px]">
                      <div>
                        <label className="text-[11px] text-gray-600 block mb-1">Status</label>
                        <select
                          className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-[11px] bg-white"
                          value={status}
                          onChange={(event) =>
                            setStatusMap((prev) => ({
                              ...prev,
                              [moduleId]: event.target.value,
                            }))
                          }
                        >
                          <option value="not_started">not_started</option>
                          <option value="in_progress">in_progress</option>
                          <option value="completed">completed</option>
                          <option value="failed">failed</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[11px] text-gray-600 block mb-1">Score</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-[11px]"
                          value={score}
                          onChange={(event) =>
                            setScoreMap((prev) => ({
                              ...prev,
                              [moduleId]: event.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => startModule(moduleItem)}
                      disabled={startModuleMutation.isLoading}
                      className="px-3 py-1.5 rounded-md text-white bg-[#0A3059] hover:bg-[#082649] text-xs inline-flex items-center gap-1 disabled:opacity-70"
                    >
                      <FiPlayCircle /> Start Module
                    </button>

                    <button
                      type="button"
                      onClick={() => saveProgress(moduleItem)}
                      disabled={saveProgressMutation.isLoading}
                      className="px-3 py-1.5 rounded-md text-[#0A3059] border border-[#0A3059] hover:bg-[#EAF0F7] text-xs inline-flex items-center gap-1 disabled:opacity-70"
                    >
                      <FiSave /> Save Progress
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {assessments.length ? (
            <>
              <h2 className="text-sm font-semibold text-gray-700 mt-5">Assessments</h2>
              {assessments.map((assessment) => (
                <div key={assessment?.id || assessment?.assessmentId} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                  <p className="text-base font-medium text-gray-800">{assessment?.title || assessment?.name || "Assessment"}</p>
                  <p className="text-[11px] text-gray-500 mt-1 capitalize">Status: {String(assessment?.status || "not_started").replaceAll("_", " ")}</p>
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => startModule(assessment, "assessment")}
                      disabled={!canTakeAssessments || startModuleMutation.isLoading}
                      className="px-3 py-1.5 rounded-md text-xs text-white bg-[#0A3059] hover:bg-[#082649] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {!canTakeAssessments ? "Locked Until 100%" : "Take Assessment"}
                    </button>
                  </div>
                </div>
              ))}
            </>
          ) : null}
        </div>
      ) : (
        <div className="bg-white p-6 rounded-xl border border-gray-100 text-sm text-gray-500">
          No modules or assessments found for this course.
        </div>
      )}
    </div>
  );
};

export default CourseModules;
